import React, { useCallback, useEffect, useState } from "react";
import { ApiRequest, ApiRequestNoLoder } from "../services/api";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";
import {
  ChevronLeft,
  ChevronRight,
  Filter,
  Clock,
  AlertCircle,
  CheckCircle,
  Zap,
  Trash2,
  RefreshCw,
  RotateCcw,
  X,
} from "lucide-react";

interface ServiceLog {
  id: number;
  service_name: string;
  worker_id: string;
  status: "running" | "success" | "failed";
  error_message: string | null;
  context: Record<string, any> | null;
  started_at: string;
  finished_at: string;
}

interface ServiceStats {
  total_logs: number;
  by_status: {
    success: number;
    failed: number;
    running: number;
  };
  by_service: Record<string, number>;
  last_24h: number;
}

export default function ServiceLogs() {
  const [logs, setLogs]           = useState<ServiceLog[]>([]);
  const [stats, setStats]         = useState<ServiceStats | null>(null);
  const [loading, setLoading]     = useState(true);
  const [page, setPage]           = useState(1);
  const [limit]                   = useState(20);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter]   = useState<string>("");
  const [serviceFilter, setServiceFilter] = useState<string>("");
  const [startDate, setStartDate]         = useState<string>("");
  const [endDate, setEndDate]             = useState<string>("");
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [selectedLog, setSelectedLog]     = useState<ServiceLog | null>(null);
  const [restartingId, setRestartingId]   = useState<number | null>(null);

  // ── Fetch logs ────────────────────────────────────────────
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { skip: (page - 1) * limit, limit };
      if (statusFilter)  params.status       = statusFilter;
      if (serviceFilter) params.service_name = serviceFilter;
      if (startDate)     params.start_date   = startDate;
      if (endDate)       params.end_date     = endDate;

      const queryString = new URLSearchParams(params).toString();
      const response = await ApiRequest("GET", `admin/service-logs/?${queryString}`);

      if (Array.isArray(response)) {
        setLogs(response);
        setTotalLogs(response.length < limit ? (page - 1) * limit + response.length : page * limit + 1);
      } else if (response?.data && Array.isArray(response.data)) {
        setLogs(response.data);
        setTotalLogs(
          response.count !== undefined
            ? response.count
            : response.data.length < limit
            ? (page - 1) * limit + response.data.length
            : page * limit + 1
        );
      } else if (response?.total !== undefined) {
        setLogs(response.data || response.logs || []);
        setTotalLogs(response.total);
      }
    } catch (error) {
      console.error("Failed to fetch service logs", error);
      toast.error("Failed to fetch service logs");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, serviceFilter, startDate, endDate]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await ApiRequest("GET", "admin/service-logs/stats");
      if (response?.total_logs !== undefined) setStats(response);
    } catch (error) {
      console.error("Failed to fetch service stats", error);
    }
  }, []);

  // ── Cleanup ───────────────────────────────────────────────
  const handleCleanup = async (days: number) => {
    if (cleanupLoading) return;
    setCleanupLoading(true);
    try {
      const response = await ApiRequest("DELETE", `admin/service-logs/cleanup?days=${days}`);
      if (response.deleted_count !== undefined) {
        toast.success(`Deleted ${response.deleted_count} logs older than ${days} days`);
        fetchLogs();
        fetchStats();
      }
    } catch (error) {
      console.error("Cleanup failed", error);
      toast.error("Cleanup operation failed");
    } finally {
      setCleanupLoading(false);
    }
  };

  // ── Restart service ───────────────────────────────────────
  const handleRestart = async (e: React.MouseEvent, log: ServiceLog) => {
    e.stopPropagation();
    if (restartingId === log.id) return;

    const uid = log.context?.user_id || log.context?.uid || "";
    const cid = log.context?.candidate_id || log.context?.cid || "";

    if (!uid) {
      toast.error("Cannot restart: no user_id found in context");
      return;
    }

    setRestartingId(log.id);
    const tid = toast.loading(`Restarting ${log.service_name}...`);
    try {
      const res = await (ApiRequestNoLoder as any)("POST", "admin/service", {
        service: log.service_name,
        uid,
        cid: cid || undefined,
      });
      if (res?.success || res?.status) {
        toast.success("Service restarted successfully", { id: tid });
        setTimeout(() => fetchLogs(), 1500);
      } else {
        toast.error(res?.message || "Restart failed", { id: tid });
      }
    } catch {
      toast.error("Restart request failed", { id: tid });
    } finally {
      setRestartingId(null);
    }
  };

  useEffect(() => { fetchLogs(); fetchStats(); }, [fetchLogs, fetchStats]);

  useEffect(() => {
    document.body.style.overflow = selectedLog ? "hidden" : "unset";
    return () => { document.body.style.overflow = "unset"; };
  }, [selectedLog]);

  const totalPages = Math.ceil(totalLogs / limit) || 1;

  // ── Helpers ───────────────────────────────────────────────
  const getStatusColor = (status: string) => {
    switch (status) {
      case "success": return "text-emerald-400 bg-emerald-500/10 border-emerald-500/20";
      case "failed":  return "text-rose-400 bg-rose-500/10 border-rose-500/20";
      case "running": return "text-blue-400 bg-blue-500/10 border-blue-500/20";
      default:        return "text-slate-400 bg-slate-500/10 border-slate-500/20";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "success": return <CheckCircle size={14} />;
      case "failed":  return <AlertCircle size={14} />;
      case "running": return <Zap size={14} className="animate-pulse" />;
      default:        return <Clock size={14} />;
    }
  };

  const formatDuration = (start: string, end: string) => {
    const duration = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
    if (duration < 60)   return `${duration}s`;
    if (duration < 3600) return `${Math.round(duration / 60)}m`;
    return `${Math.round(duration / 3600)}h`;
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-transparent p-4 md:p-8 space-y-8 font-sans">

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-slate-800 pb-10">
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-[10px] uppercase tracking-[0.2em] font-black text-green-500/80">
              Service Monitor • Live
            </span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black text-white tracking-tighter uppercase italic">
            Service Execution Logs
          </h1>
          <p className="text-slate-400 font-medium max-w-xl">
            Monitor background workers and periodic service execution history.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => fetchLogs()}
            disabled={loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white rounded-xl border border-slate-800 transition-all active:scale-95 disabled:opacity-50 group whitespace-nowrap"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : "group-hover:rotate-180 transition-transform duration-500"}`} />
            <span className="text-xs font-black uppercase tracking-wider">Refresh</span>
          </button>

          <div className="relative group">
            <button className="h-11 flex items-center gap-2 bg-red-600 border border-red-500 text-white text-xs font-black px-6 rounded-lg hover:bg-red-500 transition shadow-lg shadow-red-500/20 uppercase tracking-widest">
              <Trash2 size={14} /> Cleanup
            </button>
            <div className="absolute top-full right-0 mt-2 w-48 bg-slate-900 border border-slate-700 rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="p-2 space-y-1">
                {[7, 14, 30, 60].map((days) => (
                  <button
                    key={days}
                    onClick={() => handleCleanup(days)}
                    disabled={cleanupLoading}
                    className="w-full text-left px-4 py-2 text-xs font-bold text-white hover:bg-slate-800 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Delete logs older than {days} days
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { label: "Total Logs",  value: stats.total_logs.toLocaleString(), color: "text-white",        labelColor: "text-slate-500" },
            { label: "Success",     value: stats.by_status.success,           color: "text-emerald-400",  labelColor: "text-emerald-500" },
            { label: "Failed",      value: stats.by_status.failed,            color: "text-rose-400",     labelColor: "text-rose-500" },
            { label: "Last 24h",    value: stats.last_24h,                    color: "text-blue-400",     labelColor: "text-blue-500" },
          ].map((card, i) => (
            <motion.div key={card.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
              className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
              <div className={`text-[10px] font-black uppercase tracking-widest mb-2 ${card.labelColor}`}>{card.label}</div>
              <div className={`text-3xl font-black ${card.color}`}>{card.value}</div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Filters */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900/30 border border-slate-800 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={16} className="text-slate-400" />
          <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest">Filters</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Status</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors">
              <option value="">All Statuses</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
              <option value="running">Running</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Service Name</label>
            <input type="text" value={serviceFilter} onChange={(e) => { setServiceFilter(e.target.value); setPage(1); }}
              placeholder="Search service..."
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors placeholder-slate-600" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">End Date</label>
            <input type="date" value={endDate} onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-xs font-bold focus:outline-none focus:border-blue-500 transition-colors" />
          </div>
        </div>
      </motion.div>

      {/* Table */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900/30 border border-slate-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-800 bg-slate-950/50">
                {["Service Name", "Worker ID", "Status", "Action", "Started At", "Duration", "Error"].map((h) => (
                  <th key={h} className="px-6 py-4 text-left text-[10px] font-black text-slate-500 uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading ? (
                Array(limit).fill(0).map((_, i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan={7} className="px-6 py-4">
                      <div className="h-4 bg-slate-800 rounded w-1/4" />
                    </td>
                  </tr>
                ))
              ) : logs.length > 0 ? (
                logs.map((log) => (
                  <tr key={log.id} onClick={() => setSelectedLog(log)}
                    className="hover:bg-slate-800/30 transition-colors cursor-pointer">
                    <td className="px-6 py-4 text-sm font-bold text-white">{log.service_name}</td>
                    <td className="px-6 py-4 text-xs font-mono text-slate-400">{log.worker_id}</td>
                    <td className="px-6 py-4">
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg border text-[10px] font-black uppercase tracking-widest ${getStatusColor(log.status)}`}>
                        {getStatusIcon(log.status)}
                        {log.status}
                      </div>
                    </td>
                    {/* ── Restart button ── */}
                    <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={(e) => handleRestart(e, log)}
                        disabled={restartingId === log.id}
                        title="Re-run this service"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-[10px] font-black uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <RotateCcw size={11} className={restartingId === log.id ? "animate-spin" : ""} />
                        {restartingId === log.id ? "Running..." : "Restart"}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-400">{new Date(log.started_at).toLocaleString()}</td>
                    <td className="px-6 py-4 text-xs font-bold text-slate-400">
                      {log.finished_at ? formatDuration(log.started_at, log.finished_at) : "-"}
                    </td>
                    <td className="px-6 py-4 text-xs text-rose-400 max-w-xs truncate">{log.error_message || "-"}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="text-slate-500 text-sm font-bold">No service logs found</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {(totalPages > 1 || logs.length === limit) && (
          <div className="px-6 py-4 border-t border-slate-800 bg-slate-950/50 flex items-center justify-between sticky bottom-0 z-10">
            <div className="flex items-center gap-4">
              <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest whitespace-nowrap">
                Page {page} <span className="text-slate-700">/</span> {totalPages}
              </div>
              {loading && <div className="w-3 h-3 border-2 border-blue-500/50 border-t-blue-500 rounded-full animate-spin" />}
            </div>
            <div className="flex gap-1.5">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1 || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed">
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages || loading}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-slate-900 border border-slate-800 text-slate-500 hover:text-white hover:border-blue-500/50 hover:bg-blue-500/5 transition-all disabled:opacity-20 disabled:cursor-not-allowed">
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* Detail Modal */}
      {selectedLog && createPortal(
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000] flex items-center justify-center p-4"
          onClick={() => setSelectedLog(null)}>
          <div onClick={(e) => e.stopPropagation()}
            className="bg-slate-900 border border-slate-800 rounded-2xl max-w-2xl w-full max-h-[80vh] flex flex-col shadow-2xl overflow-hidden">

            {/* Modal header */}
            <div className="sticky top-0 bg-slate-950 border-b border-slate-800 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getStatusIcon(selectedLog.status)}
                <div>
                  <h2 className="text-lg font-black text-white">{selectedLog.service_name}</h2>
                  <p className="text-xs text-slate-400 mt-1">ID: {selectedLog.id}</p>
                </div>
              </div>
              <button onClick={() => setSelectedLog(null)} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                <X size={20} className="text-slate-400 hover:text-white" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Status</label>
                  <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-black uppercase tracking-widest ${getStatusColor(selectedLog.status)}`}>
                    {getStatusIcon(selectedLog.status)} {selectedLog.status}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Worker ID</label>
                  <p className="text-xs font-mono text-slate-300 bg-slate-950 px-3 py-2 rounded-lg border border-slate-800">{selectedLog.worker_id}</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Started At</label>
                  <p className="text-xs text-slate-300">{new Date(selectedLog.started_at).toLocaleString()}</p>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Finished At</label>
                  <p className="text-xs text-slate-300">{selectedLog.finished_at ? new Date(selectedLog.finished_at).toLocaleString() : "Still running..."}</p>
                </div>
                <div>
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Duration</label>
                  <p className="text-xs text-slate-300 font-bold">{selectedLog.finished_at ? formatDuration(selectedLog.started_at, selectedLog.finished_at) : "-"}</p>
                </div>
              </div>

              {selectedLog.error_message && (
                <div>
                  <label className="text-[10px] font-black text-rose-500 uppercase tracking-widest mb-2 block">Error Message</label>
                  <div className="bg-rose-500/5 border border-rose-500/20 rounded-lg p-4">
                    <p className="text-xs text-rose-300 font-mono whitespace-pre-wrap break-words">{selectedLog.error_message}</p>
                  </div>
                </div>
              )}

              {selectedLog.context && Object.keys(selectedLog.context).length > 0 && (
                <div>
                  <label className="text-[10px] font-black text-blue-500 uppercase tracking-widest mb-2 block">Context Data</label>
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
                    <pre className="text-xs text-blue-300 font-mono overflow-x-auto">{JSON.stringify(selectedLog.context, null, 2)}</pre>
                  </div>
                </div>
              )}
            </div>

            {/* Modal footer */}
            <div className="sticky bottom-0 bg-slate-950 border-t border-slate-800 px-6 py-4 flex items-center justify-between gap-3">
              <button
                onClick={(e) => handleRestart(e, selectedLog)}
                disabled={restartingId === selectedLog.id}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20 text-xs font-black uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw size={13} className={restartingId === selectedLog.id ? "animate-spin" : ""} />
                {restartingId === selectedLog.id ? "Restarting..." : "Restart Service"}
              </button>
              <button onClick={() => setSelectedLog(null)}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-xs font-black rounded-lg transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
